import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of, forkJoin, pipe } from 'rxjs';
import { map, catchError, mergeMap, filter, takeUntil, switchMap } from 'rxjs/operators';
import {
    ResearchScreenActionTypes, ResearchScreenInitialization, MapConfiguration,
    InitOthers, MapConfigurationSuccess, InitFilter, InitFocusAndRelationship, ResearchScreenInitializationFailed
} from '../actions/research-screen.actions';
import { AdvFilterHelperService } from '../../tamalelibs/services/adv-filter-helper.service';
import { ThreadService } from '../../tamalelibs/services/thread.service';
import { DataList } from '../../tamalelibs/models/data-list.model';
import { Thread } from '../../tamalelibs/models/thread.model';
import { EntryService } from '../../tamalelibs/services/entry.service';
import { StoreQuerierService } from '../../services/store-querier.service';
import { CustomAction } from '../actions/custom.actions';
import { ResearchScreenService } from '../../services/research-screen.service';
import {
    ResearchScreenInternalPayload, ResearchViewInfo, ResearchScreenInitOthersPayload,
    FilterInfo, ResearchScreenInitFocusRelationshipPayload
} from '../../pages/research/research.view-model';
import { ScreenActionTypes } from '../actions/screen.actions';
import { businessConstants } from '../../constants/business.constants';
import { FilterService } from '../../tamalelibs/services/filter.service';
import { ThreadEntry } from '../../tamalelibs/models/thread-entry.model';
import { ResearchFocusStatus } from '../../components/finder/finder.view-model';
@Injectable()
export class ResearchScreenEffects {
    constructor(
        private _actions$: Actions,
        private _advFilterHelper: AdvFilterHelperService,
        private _entryService: EntryService,
        private _filterService: FilterService,
        private _store: Store<any>,
        private _storeQuerier: StoreQuerierService,
        private _threadService: ThreadService,
        private _researchScreenService: ResearchScreenService,
    ) { }

    reloadThreadListEffect$: Observable<any> = createEffect(() => this._actions$.pipe(
        ofType(ResearchScreenActionTypes.RELOAD),
        mergeMap((action: CustomAction) => {
            const nextAction$ = this._actions$.pipe(
                ofType(ResearchScreenActionTypes.RELOAD),
                filter((action2: CustomAction) => {
                    // add groupId to fix expand two group one request canceled
                    return action.prefix === action2.prefix;
                }),
            );
            const pageIndex = action.payload.pageIndex;
            const pageSize = action.payload.pageSize;
            let sortBy = '';
            switch (action.payload.sortBy) {
                case 'submittedDate':
                    sortBy = action.payload.showAsMode === 1 ? 'submitteddateofthread' : 'submitteddate';
                    break;
                case 'lastModifiedDate':
                    sortBy = action.payload.showAsMode === 1 ? 'lastediteddateofthread' : 'lastediteddate';
                    break;
                case 'displayDate':
                    sortBy = action.payload.showAsMode === 1 ? 'displaydateofthread' : 'displaydate';
                    break;
                default:
                    sortBy = action.payload.sortBy;

            }

            const sortOrder = action.payload.sortOrder || 'desc';

            const advFilterArr = [];
            if (action.payload.advFilterMap) {
                Array.from(action.payload.advFilterMap.values())
                    .filter(advFilter => advFilter)
                    .forEach((advFilter: string) => advFilterArr.push(advFilter));
            }
            const advFilterStr = this._advFilterHelper.joinFiltersByAnd(advFilterArr);
            if (action.payload.showAsMode === 0) { // 0 === 'NOTES'
                return this._entryService.queryByParams(pageIndex, pageSize, sortBy,
                    sortOrder, advFilterStr, action.payload.entityIds,
                    action.payload.searchText, false, action.payload.categoryFilter, null, null, null,
                    action.payload.showCalendarEvents).pipe(
                        takeUntil(nextAction$),
                        map(response => {
                            let matchedAttachmentSet: Set<string> = new Set();
                            if (action.payload.searchText) {
                                matchedAttachmentSet = this._parseHitAttachmentIdListInEntryList(response);
                            }
                            this._store.dispatch({
                                prefix: action.prefix,
                                type: ResearchScreenActionTypes.SET_MATCHED_ATTACHMENTS,
                                payload: matchedAttachmentSet
                            });
                            const threadEntryList = this._parseEntryList(response);
                            return {
                                prefix: action.prefix,
                                type: ResearchScreenActionTypes.RELOAD_SUCCESS,
                                payload: threadEntryList
                            };
                        }),
                        catchError(err => {
                            return of({
                                prefix: action.prefix,
                                type: ResearchScreenActionTypes.RELOAD_FAILED,
                                payload: err.message
                            });
                        })
                    );
            } else {
                return this._threadService.queryByParams(pageIndex, pageSize, sortBy,
                    sortOrder, advFilterStr, action.payload.entityIds,
                    action.payload.searchText, false, action.payload.categoryFilter).pipe(
                        takeUntil(nextAction$),
                        map(response => {
                            let matchedAttachmentSet: Set<string> = new Set();
                            if (action.payload.searchText) {
                                matchedAttachmentSet = this._parseHitAttachmentIdListInThreadList(response);
                            }
                            this._store.dispatch({
                                prefix: action.prefix,
                                type: ResearchScreenActionTypes.SET_MATCHED_ATTACHMENTS,
                                payload: matchedAttachmentSet
                            });
                            const threadEntryList = this._parseThreadList(response);
                            return {
                                prefix: action.prefix,
                                type: ResearchScreenActionTypes.RELOAD_SUCCESS,
                                payload: threadEntryList
                            };
                        }),
                        catchError(err => {
                            return of({
                                prefix: action.prefix,
                                type: ResearchScreenActionTypes.RELOAD_FAILED,
                                payload: err.message
                            });
                        })
                    );
            }
        })
    ));

    reloadGroupThreadListEffect$: Observable<any> = createEffect(() => this._actions$.pipe(
        ofType(ResearchScreenActionTypes.RELOAD_GROUP),
        mergeMap((action: CustomAction) => {
            const nextAction$ = this._actions$.pipe(
                ofType(ResearchScreenActionTypes.RELOAD_GROUP),
                filter((action2: CustomAction) => {
                    // add groupId to fix expand two group one request canceled
                    return action.prefix === action2.prefix && action.payload.groupId === action2.payload.groupId;
                }),
            );
            const pageIndex = action.payload.pageIndex;
            const pageSize = action.payload.pageSize;
            const groups = action.payload.groups;
            const groupSortOrder = action.payload.groupSortOrder;
            const groupFilters = action.payload.groupFilters;
            const requestId = action.payload.requestId;
            let sortBy = '';
            switch (action.payload.sortBy) {
                case 'submittedDate':
                    sortBy = action.payload.showAsMode === 1 ? 'submitteddateofthread' : 'submitteddate';
                    break;
                case 'lastModifiedDate':
                    sortBy = action.payload.showAsMode === 1 ? 'lastediteddateofthread' : 'lastediteddate';
                    break;
                case 'displayDate':
                    sortBy = action.payload.showAsMode === 1 ? 'displaydateofthread' : 'displaydate';
                    break;
                default:
                    sortBy = action.payload.sortBy;

            }

            const sortOrder = action.payload.sortOrder || 'desc';
            const advFilterArr = [];
            if (action.payload.advFilterMap) {
                Array.from(action.payload.advFilterMap.values())
                    .filter(advFilter => advFilter)
                    .forEach((advFilter: string) => advFilterArr.push(advFilter));
            }
            const advFilterStr = this._advFilterHelper.joinFiltersByAnd(advFilterArr);
            if (action.payload.showAsMode === 0) { // 0 === 'NOTES'
                return this._entryService.queryByParams(pageIndex, pageSize, sortBy,
                    sortOrder, advFilterStr, action.payload.entityIds,
                    action.payload.searchText, false, action.payload.categoryFilter,
                    groups, groupSortOrder, groupFilters, action.payload.showCalendarEvents).pipe(
                        takeUntil(nextAction$),
                        map(response => {
                            const threadEntryList = this._parseGroupEntryList(response);
                            if (!groupFilters) {
                                // save all groups
                                this._store.dispatch({
                                    prefix: action.prefix,
                                    type: ResearchScreenActionTypes.SET_GROUP_BY_NOTE_GROUP,
                                    payload: response.groups || []
                                });
                                // clear selected node index
                                this._store.dispatch({
                                    prefix: action.prefix,
                                    type: ResearchScreenActionTypes.SET_SELECTED_INDEX,
                                    payload: null
                                });
                                this._store.dispatch({
                                    prefix: action.prefix,
                                    type: ResearchScreenActionTypes.RESET_THREAD_LIST_PAGE,
                                    payload: 1
                                });
                            } else {
                                // for server side getRow, save one group nodes
                                this._store.dispatch({
                                    prefix: action.prefix,
                                    type: ResearchScreenActionTypes.SET_GROUP_BY_NOTE_ITEMS,
                                    payload: {
                                        groupId: action.payload.groupId,
                                        requestId: requestId,
                                        threadEntryList: threadEntryList
                                    }
                                });
                                let matchedAttachmentSet: Set<string> = new Set();
                                if (action.payload.searchText) {
                                    matchedAttachmentSet = this._parseHitAttachmentIdListInGroupEntryList(response);
                                }
                                this._store.dispatch({
                                    prefix: action.prefix,
                                    type: ResearchScreenActionTypes.SET_MATCHED_ATTACHMENTS,
                                    payload: matchedAttachmentSet
                                });
                            }
                            // sync threadEntryList for other view
                            return {
                                prefix: action.prefix,
                                type: ResearchScreenActionTypes.GROUP_LOAD_SUCCESS,
                                payload: threadEntryList
                            };
                        }),
                        catchError(err => {
                            return of({
                                prefix: action.prefix,
                                type: ResearchScreenActionTypes.RELOAD_FAILED,
                                payload: err.message
                            });
                        })
                    );
            } else {
                return this._threadService.queryByParams(pageIndex, pageSize, sortBy,
                    sortOrder, advFilterStr, action.payload.entityIds,
                    action.payload.searchText, false, action.payload.categoryFilter, groups, groupSortOrder, groupFilters).pipe(
                        takeUntil(nextAction$),
                        map(response => {
                            const threadEntryList = this._parseGroupThreadList(response);
                            if (!groupFilters) {
                                // save all groups
                                this._store.dispatch({
                                    prefix: action.prefix,
                                    type: ResearchScreenActionTypes.SET_GROUP_BY_NOTE_GROUP,
                                    payload: response.groups || []
                                });
                                // clear selected node index
                                this._store.dispatch({
                                    prefix: action.prefix,
                                    type: ResearchScreenActionTypes.SET_SELECTED_INDEX,
                                    payload: null
                                });
                                this._store.dispatch({
                                    prefix: action.prefix,
                                    type: ResearchScreenActionTypes.RESET_THREAD_LIST_PAGE,
                                    payload: 1
                                });
                            } else {
                                // for server side getRow, save one group nodes
                                this._store.dispatch({
                                    prefix: action.prefix,
                                    type: ResearchScreenActionTypes.SET_GROUP_BY_NOTE_ITEMS,
                                    payload: {
                                        groupId: action.payload.groupId,
                                        threadEntryList: threadEntryList,
                                        requestId: requestId
                                    }
                                });
                                let matchedAttachmentSet: Set<string> = new Set();
                                if (action.payload.searchText) {
                                    matchedAttachmentSet = this._parseHitAttachmentIdListInGroupEntryList(response);
                                }
                                this._store.dispatch({
                                    prefix: action.prefix,
                                    type: ResearchScreenActionTypes.SET_MATCHED_ATTACHMENTS,
                                    payload: matchedAttachmentSet
                                });
                            }
                            // sync threadEntryList for other view
                            return {
                                prefix: action.prefix,
                                type: ResearchScreenActionTypes.GROUP_LOAD_SUCCESS,
                                payload: threadEntryList
                            };
                        }),
                        catchError(err => {
                            return of({
                                prefix: action.prefix,
                                type: ResearchScreenActionTypes.RELOAD_FAILED,
                                payload: err.message
                            });
                        })
                    );
            }
        })
    ));

    loadMoreThreadListEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(ResearchScreenActionTypes.LOAD_MORE),
        mergeMap((action: CustomAction) => {
            const nextAction$ = this._actions$.pipe(
                ofType(ResearchScreenActionTypes.LOAD_MORE),
                filter((action2: CustomAction) => action.prefix === action2.prefix),
            );
            const pageIndex = action.payload.pageIndex;
            const pageSize = action.payload.pageSize;
            let sortBy = '';
            switch (action.payload.sortBy) {
                case 'submittedDate':
                    sortBy = action.payload.showAsMode === 1 ? 'submitteddateofthread' : 'submitteddate';
                    break;
                case 'lastModifiedDate':
                    sortBy = action.payload.showAsMode === 1 ? 'lastediteddateofthread' : 'lastediteddate';
                    break;
                case 'displayDate':
                    sortBy = action.payload.showAsMode === 1 ? 'displaydateofthread' : 'displaydate';
                    break;
                default:
                    sortBy = action.payload.sortBy;

            }

            const sortOrder = action.payload.sortOrder || 'desc';

            const advFilterArr = [];
            if (action.payload.advFilterMap) {
                Array.from(action.payload.advFilterMap.values())
                    .filter(advFilter => advFilter)
                    .forEach((advFilter: string) => advFilterArr.push(advFilter));
            }
            const advFilterStr = this._advFilterHelper.joinFiltersByAnd(advFilterArr);

            const observables$ = [];
            if (action.payload.showAsMode === 0) { // 0 === 'NOTES'
                observables$.push(this._entryService.queryByParams(pageIndex - 1, pageSize,
                    sortBy, sortOrder, advFilterStr, action.payload.entityIds,
                    action.payload.searchText, false, action.payload.categoryFilter, null, null, null,
                    action.payload.showCalendarEvents));
                observables$.push(this._entryService.queryByParams(pageIndex,
                    pageSize, sortBy, sortOrder, advFilterStr, action.payload.entityIds,
                    action.payload.searchText, false, action.payload.categoryFilter, null, null, null,
                    action.payload.showCalendarEvents));

                return forkJoin(observables$).pipe(
                    takeUntil(nextAction$),
                    map((responses: any) => {
                        // combine two response
                        const response = {
                            count: responses[1].count,
                            next: responses[1].next
                        };
                        response['entry-list'] = responses[0]['entry-list'].concat(responses[1]['entry-list']);
                        return response;
                    }),
                    map(response => {
                        let matchedAttachmentSet: Set<string> = new Set();
                        if (action.payload.searchText) {
                            matchedAttachmentSet = this._parseHitAttachmentIdListInEntryList(response);
                        }
                        this._store.dispatch({
                            prefix: action.prefix,
                            type: ResearchScreenActionTypes.ADD_MATCHED_ATTACHMENTS,
                            payload: matchedAttachmentSet
                        });
                        const threadEntryList = this._parseEntryList(response);
                        return ({
                            prefix: action.prefix,
                            type: ResearchScreenActionTypes.LOAD_MORE_SUCCESS,
                            payload: {
                                pageIndex: pageIndex,
                                pageSize: pageSize,
                                threadEntries: threadEntryList
                            }
                        });
                    })
                );
            } else {
                observables$.push(this._threadService.queryByParams(pageIndex - 1, pageSize,
                    sortBy, sortOrder, advFilterStr, action.payload.entityIds,
                    action.payload.searchText, false, action.payload.categoryFilter));
                observables$.push(this._threadService.queryByParams(pageIndex,
                    pageSize, sortBy, sortOrder, advFilterStr, action.payload.entityIds,
                    action.payload.searchText, false, action.payload.categoryFilter));

                return forkJoin(observables$).pipe(
                    takeUntil(nextAction$),
                    map((responses: any) => {
                        // combine two response
                        const response = {
                            count: responses[1].count,
                            next: responses[1].next
                        };
                        response['thread-list'] = responses[0]['thread-list'].concat(responses[1]['thread-list']);
                        return response;
                    }),
                    map(response => {
                        let matchedAttachmentSet: Set<string> = new Set();
                        if (action.payload.searchText) {
                            matchedAttachmentSet = this._parseHitAttachmentIdListInThreadList(response);
                        }
                        this._store.dispatch({
                            prefix: action.prefix,
                            type: ResearchScreenActionTypes.ADD_MATCHED_ATTACHMENTS,
                            payload: matchedAttachmentSet
                        });
                        const threadEntryList = this._parseThreadList(response);
                        return ({
                            prefix: action.prefix,
                            type: ResearchScreenActionTypes.LOAD_MORE_SUCCESS,
                            payload: {
                                pageIndex: pageIndex,
                                pageSize: pageSize,
                                threadEntries: threadEntryList
                            }
                        });
                    })
                );
            }
        })
    ));

    loadThreadNoteBodiesEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(ResearchScreenActionTypes.GET_THREAD_NOTE_BODIES),
        mergeMap((action: any) => {
            const nextAction$ = this._actions$.pipe(
                ofType(ResearchScreenActionTypes.GET_THREAD_NOTE_BODIES),
                filter((action2: CustomAction) => action.prefix === action2.prefix),
            );

            const observables$ = [];
            action.payload.notes.forEach(note => {
                if (!note.body) {
                    const obs = this._entryService.getNoteBodyById(note.id).pipe(
                        catchError(() => {
                            return of('ERROR');
                        }));
                    observables$.push(obs);
                } else {
                    observables$.push(of('IGNORE'));
                }
            });

            return forkJoin(observables$).pipe(
                takeUntil(nextAction$),
                map(responses => {
                    const currentUser = this._storeQuerier.getCurrentUser();
                    action.payload.notes.forEach((note, index) => {
                        if (responses[index] === 'IGNORE') {
                            return;
                        } else if (responses[index] === 'ERROR') {
                            // Fixed TAM-26410, it returns empty body now.
                            // BA will design the body style if server returns 500 error later.
                            return;
                        } else {
                            if (currentUser) {
                                note.body = this._entryService.appendUserIdToImageLinksInNoteBody(responses[index], currentUser.id);
                            }
                        }
                    });
                    return ({
                        prefix: action.prefix,
                        type: ResearchScreenActionTypes.GET_THREAD_NOTE_BODIES_SUCCESS,
                        payload: action.payload
                    });
                })
            );
        })
    ));

    loadThreadRefinebyEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(ResearchScreenActionTypes.LOAD_REFINEBY),
        mergeMap((action: CustomAction) => {
            const nextAction$ = this._actions$.pipe(
                ofType(ResearchScreenActionTypes.LOAD_REFINEBY),
                filter((action2: CustomAction) => action.prefix === action2.prefix),
            );

            const advFilterArr = [];
            const _queryInfo = action.payload.queryInfo;
            const _showAsMode = action.payload.showAsMode;
            if (_queryInfo.advFilterMap) {
                Array.from(_queryInfo.advFilterMap.values())
                    .filter(advFilter => advFilter)
                    .forEach((advFilter: string) => advFilterArr.push(advFilter));
            }
            const advFilterStr = this._advFilterHelper.joinFiltersByAnd(advFilterArr);
            const category: Array<string> = _queryInfo.category;
            if (_showAsMode === 0) {  // 0 === 'NOTES
                return this._entryService.getRefinebyData(_queryInfo.entityIds,
                    _queryInfo.searchText,
                    category,
                    advFilterStr,
                    _queryInfo.categoryFilter,
                    _queryInfo.showCalendarEvents).pipe(
                        takeUntil(nextAction$),
                        map(response => {
                            const data: Map<string, Array<any>> = new Map();
                            category.forEach(id => {
                                data.set(id, response[id]);
                            });
                            return {
                                prefix: action.prefix,
                                type: ResearchScreenActionTypes.LOAD_REFINEBY_SUCCESS,
                                payload: data,
                            };
                        }),
                        catchError(err => {
                            return of({
                                prefix: action.prefix,
                                type: ResearchScreenActionTypes.LOAD_REFINEBY_FAILED,
                                payload: err.message
                            });
                        })
                    );
            } else {
                return this._threadService.getRefinebyData(_queryInfo.entityIds,
                    _queryInfo.searchText, category, advFilterStr, _queryInfo.categoryFilter).pipe(
                        takeUntil(nextAction$),
                        map(response => {
                            const data: Map<string, Array<any>> = new Map();
                            category.forEach(id => {
                                data.set(id, response[id]);
                            });
                            return {
                                prefix: action.prefix,
                                type: ResearchScreenActionTypes.LOAD_REFINEBY_SUCCESS,
                                payload: data,
                            };
                        }),
                        catchError(err => {
                            return of({
                                prefix: action.prefix,
                                type: ResearchScreenActionTypes.LOAD_REFINEBY_FAILED,
                                payload: err.message
                            });
                        })
                    );
            }
        })
    ));

    // PREPARE_SCREEN -> CONFIGURATION_LOADED   -> PAGE_INFO
    //                                          -> CONFIGURATION_MAPPED -> INIT_REFINEBY INIT_OTHERS
    //                                                                -> PREPARE FILTER ->INIT_FILTER
    //                                                                -> PREPARE FOCUS ->INIT_FOCUS INIT_RELATIONSHIP
    initializeAllEffect$: Observable<any> = createEffect(() => this._actions$.pipe(
        ofType(ResearchScreenActionTypes.INITIALIZE_ALL),
        mergeMap((action: ResearchScreenInitialization) => {
            const page = action.payload.page;
            const user = action.payload.user;
            return this._researchScreenService.loadScreenConfig(page, user).pipe(
                catchError(err => {
                    return of(null);
                }),
                switchMap((config) => {
                    if (config) {
                        const mapConfigAction = new MapConfiguration(action.prefix,
                            new ResearchScreenInternalPayload(config, page, user, null));
                        const currentPageInfo: any = {
                            id: config.id,
                            name: config.name,
                            screenType: businessConstants.screenType.research
                        };
                        if (config.id === businessConstants.allNotesScreenId || config.id === businessConstants.myNotesScreenId) {
                            currentPageInfo.type = 'BUILDIN_RESEARCH';
                        } else {
                            currentPageInfo.type = 'SAVED_RESEARCH';
                        }

                        const effectActions: any[] = [
                            mapConfigAction,
                        ];
                        if (page.withSubChrome) {
                            effectActions.push({ type: ScreenActionTypes.SET_VALUE, payload: currentPageInfo });
                        }
                        if (config && config.id) {
                            effectActions.push({ type: ScreenActionTypes.SCREEN_DICTIONARY, payload: config });
                        }
                        return effectActions;
                    } else {
                        return [new ResearchScreenInitializationFailed(action.prefix, action.payload)];
                    }
                }),
            );
        })
    ));

    mapConfigEffect$: Observable<any> = createEffect(() => this._actions$.pipe(
        ofType(ResearchScreenActionTypes.SCREEN_MAP_CONFIGURATION),
        mergeMap((action: MapConfiguration) => {
            const page = action.payload.page;
            const user = action.payload.user;
            const config = action.payload.screenConfig;
            // tslint:disable-next-line: no-shadowed-variable
            return this._researchScreenService.mapScreenConfig(config, page, user).switchMap((config: ResearchViewInfo) => {
                if (config.filterInfo && config.filterInfo.filterConfigs && config.filterInfo.filterConfigs.length > 0) {
                    config.filterInfo.filterConfigs.forEach(item => {
                        item.source = this._filterService.generateFilterSource(item.metadataType);
                    });
                }
                return [
                    new MapConfigurationSuccess(action.prefix,
                        new ResearchScreenInternalPayload(config, page, user, null)),
                    new InitOthers(action.prefix,
                        new ResearchScreenInitOthersPayload(config.layoutInfo, config.sortInfo,
                            config.keywordInfo, config.gridColumnInfo, config.refineByInfo, config.displayInfo, config.gridGroupLayout))
                ];
            }
            );
        })
    ));

    mapConfigSuccessLoadFilter$: Observable<InitFilter> = createEffect(() => this._actions$.pipe(
        ofType(ResearchScreenActionTypes.SCREEN_MAP_CONFIGURATION_SUCCESS),
        mergeMap((action: MapConfigurationSuccess) => {
            const config = action.payload.screenConfig;
            return this._researchScreenService.loadFilterFromConfig(config).pipe(
                map((filterInfo: FilterInfo) =>
                    new InitFilter(action.prefix, filterInfo)
                )
            );
        })
    ));

    mapConfigSuccessLoadFocusRelationship$: Observable<any> = createEffect(() => this._actions$.pipe(
        ofType(ResearchScreenActionTypes.SCREEN_MAP_CONFIGURATION_SUCCESS),
        mergeMap((action: MapConfigurationSuccess) => {
            const page = action.payload.page;
            const user = action.payload.user;
            const config = action.payload.screenConfig;
            return this._researchScreenService.loadFinderFromConfig(config, page, user).pipe(
                catchError(err => {
                    // entity is private or it doesn't exist in the database.
                    return of(null);
                }),
                switchMap((finder: any) => {
                    let result: Array<any>;
                    if (finder) {
                        result = [
                            new InitFocusAndRelationship(
                                action.prefix,
                                new ResearchScreenInitFocusRelationshipPayload(
                                    this._researchScreenService.loadFocusFromFinder(config, finder),
                                    this._researchScreenService.loadRelationshipFromFinder(config, page, user, finder)
                                )
                            ),
                            {
                                // store the screen entity to avoid to send duplicate request
                                type: ScreenActionTypes.SCREEN_ENTITY_DICTIONARY,
                                payload: finder
                            }
                        ];
                    } else {
                        const currentUser = this._storeQuerier.getCurrentUser();
                        const finderSetting = {
                            entity: currentUser,
                            status: ResearchFocusStatus.ALL
                        };

                        const relationship = {
                            nodes: [],
                            expandedKeys: [],
                            checkedKeys: [],
                            showAs: config.relationshipTreeInfo && config.relationshipTreeInfo.showAs ? config.relationshipTreeInfo.showAs : 'shortName'
                        };

                        result = [
                            new InitFocusAndRelationship(
                                action.prefix,
                                new ResearchScreenInitFocusRelationshipPayload(
                                    finderSetting,
                                    relationship
                                )
                            )
                        ];
                    }
                    return result;
                }),
            );
        })
    ));

    private _parseEntryList(response) {
        const threadEntryList: DataList<ThreadEntry> = new DataList<ThreadEntry>();

        threadEntryList.totalCount = response.count;
        threadEntryList.hasNext = response.next != null;
        response['entry-list'].forEach(element => {
            const threadEntry = ThreadEntry.parseEntry(element);
            threadEntryList.push(threadEntry);
        });

        return threadEntryList;
    }

    private _parseGroupEntryList(response) {
        const threadEntryList: DataList<ThreadEntry> = new DataList<ThreadEntry>();
        if (response['entry-size'] || response['entry-size'] === 0) {
            threadEntryList.totalCount = response['entry-size'];
            threadEntryList.hasNext = false;
        } else {
            threadEntryList.totalCount = null;
        }
        if (Array.isArray(response)) {
            response.forEach(group => {
                group['note-list'].forEach((element) => {
                    const threadEntry = ThreadEntry.parseEntry(element);
                    threadEntryList.push(threadEntry);
                });
            });
        }
        return threadEntryList;
    }

    private _parseGroupThreadList(response) {
        const threadEntryList: DataList<ThreadEntry> = new DataList<ThreadEntry>();
        if (response['entry-size'] || response['entry-size'] === 0) {
            threadEntryList.totalCount = response['entry-size'];
            threadEntryList.hasNext = false;
        } else {
            threadEntryList.totalCount = null;
        }
        if (Array.isArray(response)) {
            response.forEach(group => {
                group['note-list'].forEach((element) => {
                    const threadEntry = ThreadEntry.parseThread(element);
                    threadEntryList.push(threadEntry);
                });
            });
        }
        return threadEntryList;
    }

    private _parseThreadList(response) {
        const threadEntryList: DataList<ThreadEntry> = new DataList<ThreadEntry>();
        threadEntryList.totalCount = response.count;
        threadEntryList.hasNext = response.next != null;
        response['thread-list'].forEach(element => {
            const threadEntry = ThreadEntry.parseThread(element);
            threadEntryList.push(threadEntry);
        });
        return threadEntryList;
    }

    private _parseHitAttachmentIdListInGroupEntryList(response): Set<string> {
        const idSet: Set<string> = new Set<string>();
        if (response && response.length > 0) {
            response.forEach(item => {
                if (item['note-list']) {
                    item['note-list'].forEach(element => {
                        element['hits'].forEach(id => {
                            idSet.add(id);
                        });
                    });
                }
            });
        }
        return idSet;
    }

    private _parseHitAttachmentIdListInEntryList(response): Set<string> {
        const idSet: Set<string> = new Set<string>();
        response['entry-list'].forEach(element => {
            if (element['hits']) {
                element['hits'].forEach(id => {
                    idSet.add(id);
                });
            }
        });

        return idSet;
    }

    private _parseHitAttachmentIdListInThreadList(response): Set<string> {
        const idSet: Set<string> = new Set<string>();
        response['thread-list'].forEach(element => {
            if (element['hits']) {
                element['hits'].forEach(id => {
                    idSet.add(id);
                });
            }
        });

        return idSet;
    }
}
